import React, { useEffect, useState, useCallback } from "react";
import api from "../Api/base";
import {
	Box,
	Paper,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Divider,
	Pagination,
	Tabs,
	Tab,
	IconButton,
	CircularProgress,
	Alert,
	Skeleton,
	Snackbar,
	Tooltip,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
	ExpandMore,
	CalendarMonth,
	ErrorOutline,
	RefreshOutlined,
	PhotoLibrary,
	DescriptionOutlined,
} from "@mui/icons-material";
import { parse, format } from "date-fns";
import { setLoading } from "../App";
import Api from "../Api/Api.json";
import config from "../config/config";
import PhotoPrescriptionPreview from "./PhotoPrescriptionPreview";

const UploadedRecord = () => {
	const [records, setRecords] = useState([]);
	const [expandedPanel, setExpandedPanel] = useState(false);
	const [loading, setLocalLoading] = useState(true);
	const [error, setError] = useState(null);
	const [patientId, setPatientId] = useState(0);
	const [activeTabIndex, setActiveTabIndex] = useState({});
	const [page, setPage] = useState(1);
	const [pageLoading, setPageLoading] = useState(false);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "info",
	});

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const itemsPerPage = 5;
	const pageCount = Math.ceil(records.length / itemsPerPage);

	// Calculate current page items
	const currentRecords = records.slice(
		(page - 1) * itemsPerPage,
		page * itemsPerPage
	);

	// Fetch user ID
	const getUserID = useCallback(async () => {
		if (patientId) return patientId;

		try {
			const result = await api.post(Api.MUKTIBD_USER_API, {
				task: "getUserInfo",
			});

			if (result.success && result.data?.length > 0) {
				const id = result.data[0].id;
				setPatientId(id);
				return id;
			}
			throw new Error("Failed to get user information");
		} catch (error) {
			console.error("Error fetching user ID:", error);
			setError("Failed to get user information. Please try again.");
			return 0;
		}
	}, [patientId]);

	// Fetch prescription records
	const fetchRecords = useCallback(async () => {
		setLocalLoading(true);
		setError(null);

		try {
			const userId = await getUserID();
			if (!userId) {
				throw new Error("Unable to fetch user ID");
			}

			const result = await api.post(Api.PRESCRIPTION_DATA_FETCH, {
				task: "getPrescriptionPhotoList",
				patient_id: userId,
			});

			if (result.success) {
				const sortedRecords = result.data.sort((a, b) => {
					const dateA = new Date(a.creation_date);
					const dateB = new Date(b.creation_date);
					return dateB - dateA; // Sort in descending order
				});

				// Initialize tab indices
				const tabIndices = {};
				sortedRecords.forEach((record) => {
					tabIndices[record.creation_date] = 0;
				});
				setActiveTabIndex(tabIndices);

				setRecords(sortedRecords);
				if (sortedRecords.length === 0) {
					setNotification({
						open: true,
						message: "No prescription records found",
						severity: "info",
					});
				}
			} else {
				throw new Error(result.message || "Failed to fetch records");
			}
		} catch (error) {
			console.error("Error fetching records:", error);
			setError(error.message || "Failed to fetch prescription records");
		} finally {
			setLocalLoading(false);
			setLoading(false);
		}
	}, [getUserID]);

	// Handle record expansion and fetch details
	const handlePanelChange = useCallback(
		async (panel, record) => {
			setExpandedPanel(expandedPanel === panel ? false : panel);

			if (expandedPanel !== panel && !record.photo) {
				try {
					const userId = await getUserID();

					// Fetch record details in parallel
					const [photoResponse, dataResponse] = await Promise.all([
						api.post(Api.PRESCRIPTION_DATA_FETCH, {
							task: "getPrescriptionPhoto",
							patient_id: userId,
							creation_date: record.creation_date,
						}),
						api.post(Api.PRESCRIPTION_DATA_FETCH, {
							task: "getPrescriptionPhotoData",
							patient_id: userId,
							creation_date: record.creation_date,
						}),
					]);

					if (photoResponse.success && dataResponse.success) {
						setRecords((prev) =>
							prev.map((item) =>
								item.creation_date === record.creation_date
									? {
											...item,
											photo: photoResponse.data,
											prescription: dataResponse.data[0],
									  }
									: item
							)
						);
					} else {
						throw new Error("Failed to fetch record details");
					}
				} catch (error) {
					console.error("Error fetching record details:", error);
					setNotification({
						open: true,
						message: "Failed to fetch prescription details",
						severity: "error",
					});
				}
			}
		},
		[expandedPanel, getUserID]
	);

	// Handle tab change
	const handleTabChange = useCallback((date, newValue) => {
		setActiveTabIndex((prev) => ({
			...prev,
			[date]: newValue,
		}));
	}, []);

	// Handle pagination
	const handlePagination = useCallback((event, value) => {
		setPage(value);
		setPageLoading(true);
		// Simulate loading for page transition
		setTimeout(() => setPageLoading(false), 300);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	// Format date and time
	const formatDateTime = useCallback((dateTime) => {
		try {
			const parsedDate = parse(
				dateTime,
				"yyyy-MM-dd HH:mm:ss",
				new Date()
			);
			return format(parsedDate, "MMMM dd, yyyy, hh:mm a");
		} catch (error) {
			console.error("Date parsing error:", error);
			return dateTime;
		}
	}, []);

	// Close notification
	const handleCloseNotification = useCallback(() => {
		setNotification((prev) => ({ ...prev, open: false }));
	}, []);

	// Refresh records
	const handleRefresh = useCallback(() => {
		fetchRecords();
		setNotification({
			open: true,
			message: "Refreshing prescription records...",
			severity: "info",
		});
	}, [fetchRecords]);

	// Initial data fetch
	useEffect(() => {
		fetchRecords();
	}, [fetchRecords]);

	return (
		<Box
			className="max-w-3xl mx-auto px-4 py-6"
			style={{ minHeight: `calc(100vh - ${config.topbarHeight}px)` }}
		>
			<Box className="flex justify-between items-center mb-6">
				<Typography
					variant="h4"
					component="h1"
					className="font-bold text-blue-800"
				>
					ব্যাবস্থাপত্রের ছবিসমূহ
				</Typography>
				<Tooltip title="Refresh records">
					<IconButton onClick={handleRefresh} color="primary">
						<RefreshOutlined />
					</IconButton>
				</Tooltip>
			</Box>

			{loading ? (
				// Loading state
				Array.from(new Array(3)).map((_, index) => (
					<Paper key={index} className="mb-4 overflow-hidden">
						<Box className="p-4">
							<Skeleton variant="rectangular" height={60} />
						</Box>
					</Paper>
				))
			) : error ? (
				// Error state
				<Alert
					severity="error"
					action={
						<LoadingButton onClick={fetchRecords}>
							Retry
						</LoadingButton>
					}
					className="mb-4"
				>
					{error}
				</Alert>
			) : records.length === 0 ? (
				// Empty state
				<Paper className="p-6 text-center">
					<Typography variant="h6" className="mb-2 text-gray-600">
						No prescription records found
					</Typography>
					<Typography variant="body2" className="mb-4 text-gray-500">
						Any prescriptions you upload will appear here
					</Typography>
					<LoadingButton
						variant="outlined"
						onClick={handleRefresh}
						startIcon={<RefreshOutlined />}
					>
						Refresh
					</LoadingButton>
				</Paper>
			) : (
				// Records list
				<>
					{pageLoading
						? // Page loading state
						  Array.from(new Array(3)).map((_, index) => (
								<Paper
									key={index}
									className="mb-4 overflow-hidden"
								>
									<Box className="p-4">
										<Skeleton
											variant="rectangular"
											height={60}
										/>
									</Box>
								</Paper>
						  ))
						: // Records display
						  currentRecords.map((record, index) => (
								<Accordion
									key={record.creation_date}
									expanded={
										expandedPanel === record.creation_date
									}
									onChange={() =>
										handlePanelChange(
											record.creation_date,
											record
										)
									}
									className="mb-4 shadow-md rounded-lg overflow-hidden border border-gray-100"
								>
									<AccordionSummary
										expandIcon={
											<IconButton
												size="small"
												className="bg-blue-50"
											>
												<ExpandMore />
											</IconButton>
										}
										className="hover:bg-blue-50 transition-colors"
									>
										<Box className="flex items-center w-full gap-3">
											<CalendarMonth className="text-blue-600" />
											<Typography
												variant="subtitle1"
												className="font-medium"
											>
												{formatDateTime(
													record.creation_date
												)}
											</Typography>
										</Box>
									</AccordionSummary>

									<Divider />

									<AccordionDetails className="p-0">
										{/* Tabs */}
										<Box
											sx={{
												borderBottom: 1,
												borderColor: "divider",
											}}
										>
											<Tabs
												value={
													activeTabIndex[
														record.creation_date
													] || 0
												}
												onChange={(e, newValue) =>
													handleTabChange(
														record.creation_date,
														newValue
													)
												}
												variant="fullWidth"
												className="bg-gray-50"
											>
												<Tab
													icon={
														<PhotoLibrary fontSize="small" />
													}
													iconPosition="start"
													label={
														isMobile
															? "Photo"
															: "Prescription Photo"
													}
													className="font-medium"
												/>
												<Tab
													icon={
														<DescriptionOutlined fontSize="small" />
													}
													iconPosition="start"
													label={
														isMobile
															? "Digital"
															: "Digital Prescription"
													}
													className="font-medium"
												/>
											</Tabs>
										</Box>

										{/* Tab Content */}
										<Box className="p-4">
											{!record.photo ? (
												<Box className="flex justify-center p-6">
													<CircularProgress
														size={40}
													/>
												</Box>
											) : (
												<>
													{activeTabIndex[
														record.creation_date
													] === 0 && (
														<Box className="flex justify-center">
															<img
																src={
																	record.photo
																}
																alt="Prescription"
																className="max-w-full h-auto rounded-lg border border-gray-200"
																loading="lazy"
															/>
														</Box>
													)}

													{activeTabIndex[
														record.creation_date
													] === 1 && (
														<PhotoPrescriptionPreview
															prescription={
																record.prescription
															}
														/>
													)}
												</>
											)}
										</Box>
									</AccordionDetails>
								</Accordion>
						  ))}

					{/* Pagination */}
					{pageCount > 1 && (
						<Box className="mt-8 flex justify-center">
							<Pagination
								size="medium"
								count={pageCount}
								color="primary"
								onChange={handlePagination}
								siblingCount={isMobile ? 0 : 1}
								page={page}
								showFirstButton={!isMobile}
								showLastButton={!isMobile}
							/>
						</Box>
					)}
				</>
			)}

			{/* Notification */}
			<Snackbar
				open={notification.open}
				autoHideDuration={5000}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant="filled"
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default UploadedRecord;
